// Header / format notes below must stay aligned with server import detection:
// server/src/import_file/parse.rs (detect_column_indexes, analyze_csv),
// server/src/import_file/formats/{bc_sot.rs, blue_planner.rs, deduction_list.rs, sage_gl.rs, detector.rs}.

import { capitalCase } from "change-case"
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react"
import { useState } from "preact/hooks"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"

export function label_for_file_import_format(import_format: string | undefined): string {
  if (!import_format) {
    return ""
  }
  if (import_format === "bc_sot_detail") {
    return "Business Central SoT detail"
  }
  if (import_format === "blue_planner_html") {
    return "Blue Planner (HTML export)"
  }
  if (import_format === "deduction_list") {
    return "Deduction list (check detail)"
  }
  return capitalCase(import_format.replaceAll("_", " "))
}

export interface FileImportHelpProps {
  className?: string
  /** Both list and detail pages pass false so the grid/upload area is prioritized; expand on click. */
  defaultOpen?: boolean
  /** When set (import detail), shows which format the server detected for this file. */
  detectedFormat?: string
}

export function FileImportHelp({ className, defaultOpen = true, detectedFormat }: FileImportHelpProps) {
  const [open, setOpen] = useState(defaultOpen)
  const detected_label = label_for_file_import_format(detectedFormat)

  return (
    <Collapsible open={open} onOpenChange={setOpen} class={className}>
      <CollapsibleTrigger class="w-full rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 px-4 py-3 flex items-center gap-2 text-left transition-colors">
        <HelpCircle className="h-4 w-4 text-gray-500 shrink-0" />
        <span class="text-sm font-medium text-gray-900">How file import works</span>
        {open ? (
          <ChevronDown className="h-4 w-4 ml-auto shrink-0 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 ml-auto shrink-0 text-gray-500" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div class="mt-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 space-y-5 leading-relaxed">
          {detected_label ? (
            <p class="text-gray-800 font-medium border-b border-gray-100 pb-3">
              This upload was detected as: <span class="text-blue-700">{detected_label}</span>.
            </p>
          ) : null}

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Two import paths</h3>
            <ul class="list-disc pl-5 space-y-1">
              <li>
                <strong>CSV or Excel (ledger)</strong> — Rows are stored as an import preview. You review and code
                lines, then use <strong>Import Deductions</strong> to create real deductions in MarginWiz (or link to
                existing ones). Use <strong>Export Coding</strong> to download a CSV that includes your split / account
                columns alongside the original file columns.
              </li>
              <li>
                <strong>PDF or image (AI)</strong> — A separate flow on this page: AI extracts line items; you confirm
                before deductions are saved. It does not use the ledger column rules below.
              </li>
            </ul>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Structured import steps</h3>
            <ol class="list-decimal pl-5 space-y-1">
              <li>Upload a file — the server parses each line into import rows (not deductions yet).</li>
              <li>
                Fix invoice numbers, kinds, links to existing deductions, and ledger splits as needed (optional filters
                and bulk actions on the grid).
              </li>
              <li>
                Click <strong>Import Deductions</strong> — with rows selected, only those rows are imported; with none
                selected, all eligible rows run. Rows already linked to a deduction are skipped. When the run
                finishes, the <strong>Import results</strong> dialog lists each row: new deduction, linked to an
                existing deduction, or skipped (with a short reason).
              </li>
              <li>
                Click <strong>Export Coding</strong> — downloads an enriched CSV (original headers plus Class, account
                code, and split amount columns when splits exist).
              </li>
            </ol>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Generic CSV / Excel (posting detail)
            </h3>
            <p class="mb-2">
              The first row must be column headers. The importer looks for columns by name (case-insensitive, extra
              spaces OK):
            </p>
            <ul class="list-disc pl-5 space-y-1">
              <li>
                <strong>Date</strong> — e.g. <code class="text-xs bg-gray-100 px-1 rounded">Posting Date</code>,{" "}
                <code class="text-xs bg-gray-100 px-1 rounded">Posted dt.</code>, or another column whose header
                contains <code class="text-xs bg-gray-100 px-1 rounded">date</code> but not invoice, due, or execution
                dates.
              </li>
              <li>
                <strong>Memo / description</strong> — header contains <code class="text-xs bg-gray-100 px-1 rounded">memo</code>{" "}
                or <code class="text-xs bg-gray-100 px-1 rounded">description</code> (not &quot;account&quot; in the
                header).
              </li>
              <li>
                <strong>Customer / distributor</strong> —{" "}
                <code class="text-xs bg-gray-100 px-1 rounded">Customer name</code> is preferred when present; otherwise{" "}
                <code class="text-xs bg-gray-100 px-1 rounded">Customer</code>, headers starting with{" "}
                <code class="text-xs bg-gray-100 px-1 rounded">cust</code>, or <code class="text-xs bg-gray-100 px-1 rounded">client</code> /{" "}
                <code class="text-xs bg-gray-100 px-1 rounded">source name</code>.
              </li>
              <li>
                <strong>Debit and Credit</strong> — separate columns or a single column whose header contains both
                words (e.g. <code class="text-xs bg-gray-100 px-1 rounded">Debit/(Credit)</code>).
              </li>
              <li>
                Optional: <code class="text-xs bg-gray-100 px-1 rounded">Amount ($)</code> when it is not labeled as
                debit/credit; <code class="text-xs bg-gray-100 px-1 rounded">Repay / Deduct</code> for row kind hints.
              </li>
              <li>
                Optional: a column whose header is exactly <code class="text-xs bg-gray-100 px-1 rounded">Doc</code> —
                combined with memo for extracting invoice numbers when the memo line has no INV-style token.
              </li>
            </ul>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Sage General Ledger CSV</h3>
            <p>
              Exports that start with metadata rows (Company name, Report name, date range) are supported: the real
              header row is detected when the first cell is <code class="text-xs bg-gray-100 px-1 rounded">G/L Account</code>{" "}
              (or the row contains posted-date and debit-style headers). Data rows follow that header.
            </p>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Business Central SoT detail</h3>
            <p class="mb-2">CSV must include all of these headers (exact names, case-insensitive):</p>
            <ul class="list-disc pl-5 space-y-1 font-mono text-xs text-gray-800">
              <li>Posting Date</li>
              <li>Source Name</li>
              <li>Description</li>
              <li>Repay / Deduct</li>
              <li>G/L Account No.</li>
              <li>Amount ($)</li>
              <li>Debit Amount ($)</li>
              <li>Credit Amount ($)</li>
            </ul>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Deduction list (check detail)</h3>
            <p class="mb-2">
              CSV or Excel with one row per deduction taken on a customer check (e.g. HEB, Walmart, or Costco
              deduction lists). Requires all of these headers (case-insensitive, extra spaces OK):
            </p>
            <ul class="list-disc pl-5 space-y-1 font-mono text-xs text-gray-800">
              <li>Deposit Date</li>
              <li>Customer</li>
              <li>Check Date</li>
              <li>Check #</li>
              <li>Invoice Date</li>
              <li>Invoice #</li>
              <li>Amount</li>
            </ul>
            <p class="mt-2">
              Optional: <code class="text-xs bg-gray-100 px-1 rounded">Method (AP/AR)</code> (values like Deduct /
              Repay) and <code class="text-xs bg-gray-100 px-1 rounded">Description</code>. Amounts are signed:
              negative rows become deductions, positive rows become repayments. Invoice #, Check #, Check Date, and
              Invoice Date are taken from their columns directly.
            </p>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Blue Planner</h3>
            <p class="mb-2">Requires these columns:</p>
            <ul class="list-disc pl-5 space-y-1 font-mono text-xs text-gray-800">
              <li>Invoice Date</li>
              <li>Item Description</li>
              <li>Customer Name</li>
              <li>Extended Price</li>
            </ul>
            <p class="mt-2">
              HTML-as-.xls exports, CSV, and XLSX variants are auto-detected when the file matches Blue Planner&apos;s
              shape.
            </p>
          </section>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
